import { apiClient } from '@/shared/api/client';

/**
 * Malaysia SSM Scraping 공통 API
 * - SSM 세션 관리 (로그인/로그아웃)
 * - DB에 유효 세션 있으면 재사용 (10분 캐싱)
 *
 * 엔드포인트: /api/marketplace/v1/malaysia/*
 */

/**
 * SSM 로그인 응답 타입
 */
export interface SsmLoginResponse {
  success: boolean;
  session_id?: string;
  user_name?: string;
  from_cache?: boolean; // DB 캐시에서 가져온 세션인지 여부
  error?: string;
}

/**
 * SSM 로그인 타입
 * - mycoid2016: 일반 로그인 (회사명 예약, Entity 조회 등)
 * - idpro: SSM4U 로그인 (beneficial owners 조회 등)
 */
export type SsmLoginType = 'mycoid2016' | 'idpro';

/**
 * Malaysia Scraping 공통 API
 */
export const malaysiaScraping = {
  /**
   * SSM 로그인
   * - credential_id 전달 시 백엔드에서 자동 복호화
   * - DB에 유효 세션(10분 이내) 있으면 재사용
   *
   * @param credentialId - L_REGISTRY_CREDENTIAL.CREDENTIAL_ID
   * @param loginType - 로그인 타입 (기본: mycoid2016)
   */
  ssmLogin: async (credentialId: string, loginType: SsmLoginType = 'mycoid2016'): Promise<SsmLoginResponse> => {
    console.log('🔐 [MalaysiaScraping] SSM Login request:', { credentialId, loginType });

    const response = await apiClient.post<SsmLoginResponse>('/marketplace/v1/malaysia/ssm-session/login', {
      credential_id: credentialId,
      login_type: loginType
    });

    const result = (response as any)?.data || response;

    console.log('🔍 [MalaysiaScraping] SSM Login response:', {
      success: result.success,
      hasSessionId: !!result.session_id,
      fromCache: result.from_cache,
      error: result.error
    });

    return result;
  },

  /**
   * SSM 로그아웃
   * @param sessionId - SSM session_id
   */
  ssmLogout: async (sessionId: string): Promise<{ success: boolean; error?: string }> => {
    console.log('🔓 [MalaysiaScraping] SSM Logout request:', { sessionId });

    const response = await apiClient.post<{ success: boolean; error?: string }>('/marketplace/v1/malaysia/ssm-session/logout', {
      session_id: sessionId
    });

    const result = (response as any)?.data || response;

    console.log('🔍 [MalaysiaScraping] SSM Logout response:', result);

    return result;
  },

  /**
   * SSM 헬스체크
   */
  healthCheck: async (): Promise<{ success: boolean; pythonServer?: string; error?: string }> => {
    try {
      const response = await apiClient.get<any>('/marketplace/v1/malaysia/health');
      return (response as any)?.data || response;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Health check failed'
      };
    }
  }
};

export default malaysiaScraping;
